import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ClientCombobox from "@/components/ClientCombobox";
import { Plus, Trash, MagicWand, Stack, MapPinPlus, Lightning } from "@phosphor-icons/react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

const emptyItem = () => ({ service_id: null, description: "", quantity: 1, unit_price: 0 });
const emptyTarget = () => ({ _k: Math.random().toString(36).slice(2), client_id: "", property_id: "", scheduled_date: "", scheduled_time: "", assigned_crew_ids: [] });

export default function BulkJobDialog({ open, onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [crew, setCrew] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [title, setTitle] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [targets, setTargets] = useState([emptyTarget()]);
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.listClients().then(setClients).catch(() => {});
    api.listServices().then(setServices).catch(() => {});
    api.listUsers().then(setCrew).catch(() => {});
    api.listTemplates().then(setTemplates).catch(() => {});
    setTitle(""); setItems([emptyItem()]); setTaxRate(0); setNotes("");
    setTargets([emptyTarget()]); setSaveTemplate(false); setTemplateName("");
  }, [open]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0), [items]);
  const total = subtotal * (1 + Number(taxRate) / 100);

  const updateItem = (idx, patch) => setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const pickService = (idx, sid) => { const s = services.find((x) => x.id === sid); if (s) updateItem(idx, { service_id: s.id, description: s.name, unit_price: s.unit_price }); };

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setTitle(t.title || t.name);
    setItems(t.line_items?.length ? t.line_items.map((i) => ({ ...i })) : [emptyItem()]);
    setTaxRate(t.tax_rate || 0);
    setNotes(t.notes || "");
    toast.success(`Loaded template "${t.name}"`);
  };

  const updateTarget = (k, patch) => setTargets((p) => p.map((t) => (t._k === k ? { ...t, ...patch } : t)));
  const toggleCrew = (k, cid) => setTargets((p) => p.map((t) => t._k === k ? { ...t, assigned_crew_ids: t.assigned_crew_ids.includes(cid) ? t.assigned_crew_ids.filter((x) => x !== cid) : [...t.assigned_crew_ids, cid] } : t));

  const submit = async () => {
    if (!title.trim()) return toast.error("Add a job title");
    const cleanItems = items.filter((i) => i.description.trim()).map((i) => ({ service_id: i.service_id || null, description: i.description, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 }));
    if (!cleanItems.length) return toast.error("Add at least one line item");
    const cleanTargets = targets.filter((t) => t.client_id).map((t) => ({ client_id: t.client_id, property_id: t.property_id || null, scheduled_date: t.scheduled_date || null, scheduled_time: t.scheduled_time || null, assigned_crew_ids: t.assigned_crew_ids }));
    if (!cleanTargets.length) return toast.error("Add at least one address/client");
    if (saveTemplate && !templateName.trim()) return toast.error("Name your template");

    setSaving(true);
    try {
      const res = await api.bulkJobs({ title, line_items: cleanItems, tax_rate: Number(taxRate) || 0, notes, targets: cleanTargets, save_as_template: saveTemplate, template_name: templateName });
      toast.success(`${res.created} jobs created${saveTemplate ? " + template saved" : ""}`);
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create jobs"); }
    finally { setSaving(false); }
  };

  const crewMembers = crew.filter((u) => u.role === "crew");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="bulk-job-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><Lightning size={22} weight="fill" className="text-[#00B4D8]" /> Bulk / Similar Jobs</DialogTitle>
          <p className="text-sm text-slate-500">Define the work once, then apply it to as many addresses as you need.</p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Template loader */}
          {templates.length > 0 && (
            <div className="flex items-center gap-2 bg-cyan-50 border border-cyan-200 rounded-md p-3">
              <Stack size={18} className="text-[#00B4D8]" />
              <span className="text-sm font-medium text-slate-700">Start from a template:</span>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger className="w-56 bg-white" data-testid="template-select"><SelectValue placeholder="Choose template…" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {/* The work */}
          <div className="space-y-4">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">1 · The Work</h4>
            <div><Label>Job Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Quarterly Driveway Wash" className="mt-1.5" data-testid="bulk-title-input" /></div>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="grid grid-cols-12 gap-2 bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500"><div className="col-span-6">Description</div><div className="col-span-2 text-center">Qty</div><div className="col-span-2 text-right">Rate</div><div className="col-span-2 text-right">Amount</div></div>
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 items-center">
                  <div className="col-span-6 flex gap-1.5">
                    {services.length > 0 && (
                      <Select value={it.service_id || ""} onValueChange={(v) => pickService(idx, v)}>
                        <SelectTrigger className="w-10 px-0 justify-center shrink-0"><MagicWand size={15} /></SelectTrigger>
                        <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {money(s.unit_price)}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                    <Input value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value, service_id: null })} placeholder="Service" data-testid={`bulk-item-desc-${idx}`} />
                  </div>
                  <Input type="number" className="col-span-2 text-center" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} />
                  <Input type="number" className="col-span-2 text-right" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} />
                  <div className="col-span-2 flex items-center justify-end gap-1"><span className="text-sm font-semibold">{money(it.quantity * it.unit_price)}</span><button onClick={() => setItems((p) => p.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-rose-500"><Trash size={14} /></button></div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setItems((p) => [...p, emptyItem()])} className="text-[#00B4D8]" data-testid="bulk-add-item"><Plus size={16} className="mr-1" /> Add line item</Button>
              <div className="flex items-center gap-2 text-sm"><span className="text-slate-500">Tax</span><Input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-16 h-8 text-right" /><span className="text-slate-400">%</span><span className="ml-2 font-semibold">Each: {money(total)}</span></div>
            </div>
            <div><Label>Notes (applied to every job)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 h-16" /></div>
          </div>

          {/* Targets */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">2 · Addresses ({targets.length})</h4>
              <Button variant="outline" size="sm" onClick={() => setTargets((p) => [...p, emptyTarget()])} data-testid="bulk-add-target"><MapPinPlus size={16} className="mr-1" /> Add address</Button>
            </div>
            {targets.map((t, ti) => {
              const c = clients.find((x) => x.id === t.client_id);
              const props = c?.properties || [];
              return (
                <div key={t._k} className="border border-slate-200 rounded-md p-3 bg-slate-50/60" data-testid={`bulk-target-${ti}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Client</Label>
                      <ClientCombobox clients={clients} value={t.client_id} onChange={(v) => updateTarget(t._k, { client_id: v, property_id: "" })} testid={`bulk-client-${ti}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Property</Label>
                      <Select value={t.property_id} onValueChange={(v) => updateTarget(t._k, { property_id: v })} disabled={!props.length}>
                        <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder={props.length ? "Select property" : "No properties"} /></SelectTrigger>
                        <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Date</Label><Input type="date" value={t.scheduled_date} onChange={(e) => updateTarget(t._k, { scheduled_date: e.target.value })} className="mt-1 bg-white" /></div>
                    <div><Label className="text-xs">Time</Label><Input type="time" value={t.scheduled_time} onChange={(e) => updateTarget(t._k, { scheduled_time: e.target.value })} className="mt-1 bg-white" /></div>
                  </div>
                  {crewMembers.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                      <span className="text-xs text-slate-500">Crew:</span>
                      {crewMembers.map((u) => (
                        <button key={u.id} type="button" onClick={() => toggleCrew(t._k, u.id)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors duration-200 ${t.assigned_crew_ids.includes(u.id) ? "bg-[#0B1726] text-white border-[#0B1726]" : "bg-white text-slate-600 border-slate-300"}`}>{u.name}</button>
                      ))}
                    </div>
                  )}
                  {targets.length > 1 && <button onClick={() => setTargets((p) => p.filter((x) => x._k !== t._k))} className="text-xs text-rose-500 hover:underline mt-2">Remove address</button>}
                </div>
              );
            })}
          </div>

          {/* Save template */}
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md p-3">
            <Switch checked={saveTemplate} onCheckedChange={setSaveTemplate} data-testid="save-template-switch" />
            <span className="text-sm text-slate-600">Save this work as a reusable template</span>
            {saveTemplate && <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Template name" className="ml-auto w-56 bg-white h-9" data-testid="template-name-input" />}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="bulk-submit">
            <Lightning size={16} weight="fill" className="mr-1.5" /> {saving ? "Creating…" : `Create ${targets.filter((t) => t.client_id).length} Jobs`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
